import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, Link } from 'react-router-dom'
import PageShell from '../components/Layout/PageShell'
import PortalHeader from '../components/Layout/PortalHeader'
import {
  PortalStatCard,
  PortalTaskItem,
  PortalNotifItem,
  PortalActivityItem,
  QuickAction,
  CardTitle,
} from '../components/Layout/portalUi'
import DigestTicker from '../components/DigestTicker'
import { RsIcon } from '../components/icons'
import { STAT_ICON_MAP } from '../components/icons/registry'
import axios from 'axios'
import { getCmsIcon } from '../utils/cmsIcons'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import { formatRelativeTimeAgo } from '../utils/relativeTime'

const STAT_META = {
  deals: { icon: 'deal', color: 'blue' },
  lps: { icon: 'users', color: 'gold' },
  raised: { icon: 'dollar', color: 'green' },
  dd: { icon: 'activity', color: 'red' },
  portfolio: { icon: 'portfolio', color: 'blue' },
  exposure: { icon: 'dollar', color: 'gold' },
  chat: { icon: 'chat', color: 'blue' },
  plan: { icon: 'shield', color: 'green' },
  files: { icon: 'files', color: 'blue' },
  meetings: { icon: 'meeting', color: 'gold' },
  funding: { icon: 'dollar', color: 'green' },
  contacts: { icon: 'users', color: 'purple' },
  clients: { icon: 'users', color: 'gold' },
  storage: { icon: 'folder', color: 'blue' },
  access: { icon: 'shield', color: 'green' },
}

const PROJECT_HEADERS = {
  GP: { col2: 'Industry', title: 'My Active Deals' },
  LP: { col2: 'GP', title: 'Portfolio Overview' },
  Startup: { col2: 'Type', title: 'Asset Listings' },
  ServiceProvider: { col2: 'Phase', title: 'Active Projects' },
}

const NOTIF_TYPE_META = {
  chat: { icon: 'chat', bg: '#e8eeff' },
  meeting: { icon: 'meeting', bg: '#fef0d0' },
}

function formatNotifTime(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return formatRelativeTimeAgo(d)
}

function DashboardNotificationsCard({
  notifications,
  loading,
  onMarkAllRead,
  onOpen,
}) {
  return (
    <div className="portal-card">
      <div className="portal-card-header">
        <CardTitle icon="bell">Notifications</CardTitle>
        <button
          type="button"
          className="portal-card-action"
          disabled={!notifications.length}
          onClick={onMarkAllRead}
        >
          Mark all read
        </button>
      </div>
      <div className="portal-card-body">
        {loading && <p style={{ color: '#909399', fontSize: 13 }}>Loading notifications…</p>}
        {!loading && notifications.length ? notifications.map((n) => {
          const meta = NOTIF_TYPE_META[n.type] || { icon: 'bell', bg: '#f5f7fa' }
          return (
            <PortalNotifItem
              key={n.id}
              icon={meta.icon}
              bg={meta.bg}
              title={n.title}
              time={n.preview || formatNotifTime(n.time)}
              unread
              onClick={() => onOpen(n)}
            />
          )
        }) : null}
        {!loading && !notifications.length && (
          <p style={{ color: '#909399', fontSize: 13 }}>No new notifications</p>
        )}
      </div>
    </div>
  )
}

function taskPriority(progress, phase) {
  if (progress >= 70 || /escrow|term/i.test(phase || '')) return 'urgent'
  if (progress >= 40 || /dd|nda/i.test(phase || '')) return 'high'
  return 'normal'
}

function AdvisorDashboard({
  role,
  loading,
  displayStats,
  projects,
  recentMessages,
  upcomingMeetings,
  navigate,
  user,
  notifications,
  notificationsLoading,
  onMarkAllNotifications,
  onOpenNotification,
}) {
  const welcome =
    role === 'Startup'
      ? 'Welcome back, Startup'
      : role === 'ServiceProvider'
        ? 'Welcome back, Service Provider'
        : `Welcome, ${user?.system_name || user?.real_name}`

  const tasks = projects.length
    ? projects.map((p) => ({
      title: p.title,
      meta: `${p.subtitle || 'Project'} · ${(p.phase || 'active').replace(/_/g, ' ')}`,
      priority: taskPriority(p.progress, p.phase),
    }))
    : [{ title: 'Review pending documents', meta: 'General · Documentation', priority: 'normal' }]

  const quickActions =
    role === 'Startup' ? (
      <>
        <QuickAction to="/deals" icon="dollar">View Funding Status</QuickAction>
        <QuickAction to="/deals?tab=browse" icon="search">Find Investors</QuickAction>
        <QuickAction icon="clipboard" onClick={() => navigate('/deals')}>Pending Tasks</QuickAction>
        <QuickAction to="/chat" icon="meeting">Upcoming Meetings</QuickAction>
      </>
    ) : (
      <>
        <QuickAction icon="clipboard" onClick={() => navigate('/deals')}>Pending Tasks</QuickAction>
        <QuickAction to="/chat" icon="meeting">Upcoming Meetings</QuickAction>
        <QuickAction to="/documents" icon="folder">Recent Files</QuickAction>
        <QuickAction to="/email-campaigns" icon="email">Email</QuickAction>
      </>
    )

  return (
    <>
      <DigestTicker linkToPage />
      <PortalHeader title={welcome} showSearch />

      <div className="portal-quick-actions">{quickActions}</div>

      <div className="portal-stats-row">
        {loading && (
          <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#909399' }}>Loading…</p>
        )}
        {!loading &&
          displayStats.map((stat) => (
            <PortalStatCard
              key={stat.key || stat.label}
              statKey={stat.key}
              label={stat.label}
              value={stat.value}
              change={stat.change}
            />
          ))}
      </div>

      <div className="portal-content-stack">
        <DashboardNotificationsCard
          notifications={notifications}
          loading={notificationsLoading}
          onMarkAllRead={onMarkAllNotifications}
          onOpen={onOpenNotification}
        />

        <div className="portal-card">
          <div className="portal-card-header">
            <CardTitle icon="zap">Pending Tasks</CardTitle>
            <button type="button" className="portal-card-action" onClick={() => navigate('/deals')}>
              View All
            </button>
          </div>
          <div className="portal-card-body">
            {tasks.map((t, i) => (
              <PortalTaskItem key={i} {...t} />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function GpLpDashboard({
  user,
  role,
  loading,
  displayStats,
  projects,
  recentMessages,
  upcomingMeetings,
  pipelineStats,
  navigate,
  notifications,
  notificationsLoading,
  onMarkAllNotifications,
  onOpenNotification,
}) {
  const welcome =
    role === 'GP'
      ? `Welcome back, ${user?.system_name || user?.real_name || 'GP Partner'}`
      : `Welcome back, ${user?.system_name || user?.real_name || 'LP Partner'}`

  const tasks = projects.length
    ? projects.map((p) => ({
      title: p.title,
      meta: `${p.subtitle || 'Deal'} · ${(p.phase || 'active').replace(/_/g, ' ')}`,
      priority: taskPriority(p.progress, p.phase),
    }))
    : [
      { title: 'Review pending deal documents', meta: 'General · Documentation', priority: 'normal' },
    ]

  const quickActions = role === 'GP' ? (
    <>
      <QuickAction to="/deals?action=create" icon="megaphone">Post New Deal</QuickAction>
      <QuickAction to="/deals?tab=browse" icon="search">Browse Deals</QuickAction>
      <QuickAction icon="clipboard" onClick={() => navigate('/deals')}>Pending Tasks</QuickAction>
      <QuickAction to="/email-campaigns" icon="email">Email</QuickAction>
    </>
  ) : (
    <>
      <QuickAction icon="trendUp" onClick={() => navigate('/deals')}>View My Investments</QuickAction>
      <QuickAction icon="clipboard" onClick={() => navigate('/deals')}>Pending Tasks</QuickAction>
      <QuickAction to="/email-campaigns" icon="email">Email</QuickAction>
      <QuickAction to="/deals?tab=browse" icon="search" className="primary">Find a Deal</QuickAction>
    </>
  )

  return (
    <>
      <DigestTicker linkToPage />
      <PortalHeader title={welcome} showSearch />

      <div className="portal-quick-actions">{quickActions}</div>

      <div className="portal-stats-row">
        {loading && <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#909399' }}>Loading…</p>}
        {!loading && displayStats.map((stat) => (
          <PortalStatCard
            key={stat.key || stat.label}
            statKey={stat.key}
            label={stat.label}
            value={stat.value}
            change={stat.change}
          />
        ))}
      </div>

      {role === 'GP' ? (
        <div className="portal-content-stack">
          <DashboardNotificationsCard
            notifications={notifications}
            loading={notificationsLoading}
            onMarkAllRead={onMarkAllNotifications}
            onOpen={onOpenNotification}
          />

          <div className="portal-card">
            <div className="portal-card-header">
              <CardTitle icon="zap">Pending Tasks</CardTitle>
              <button type="button" className="portal-card-action" onClick={() => navigate('/deals')}>View All</button>
            </div>
            <div className="portal-card-body">
              {tasks.map((t, i) => (
                <PortalTaskItem key={i} {...t} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="portal-content-grid">
          <div>
            <div className="portal-card">
              <div className="portal-card-header">
                <CardTitle icon="zap">Pending Tasks</CardTitle>
                <button type="button" className="portal-card-action" onClick={() => navigate('/deals')}>View All</button>
              </div>
              <div className="portal-card-body">
                {tasks.map((t, i) => (
                  <PortalTaskItem key={i} {...t} />
                ))}
              </div>
            </div>

            <div className="portal-card">
              <div className="portal-card-header">
                <CardTitle icon="megaphone">Recent Activity</CardTitle>
                <button type="button" className="portal-card-action" onClick={() => navigate('/chat')}>View All</button>
              </div>
              <div className="portal-card-body">
                {recentMessages.length ? recentMessages.map((m, i) => (
                  <PortalActivityItem key={i} dot={i % 2 ? 'green' : 'blue'} time={formatRelativeTimeAgo(m.time)}>
                    <strong>{m.from}</strong> — {m.text}
                  </PortalActivityItem>
                )) : (
                  <p style={{ color: '#909399', fontSize: 13 }}>No recent activity</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 24 }}>
              <DashboardNotificationsCard
                notifications={notifications}
                loading={notificationsLoading}
                onMarkAllRead={onMarkAllNotifications}
                onOpen={onOpenNotification}
              />
            </div>

            <div className="portal-card">
              <div className="portal-card-header">
                <CardTitle icon="chart">Deals Progress</CardTitle>
              </div>
              <div className="portal-card-body">
                {pipelineStats?.byPhase && Object.keys(pipelineStats.byPhase).length > 0 ? (
                  Object.entries(pipelineStats.byPhase).slice(0, 4).map(([phase, count]) => {
                    const pct = Math.round((count / (pipelineStats.total || 1)) * 100)
                    return (
                      <div key={phase} className="portal-progress-row">
                        <div className="portal-progress-label">
                          <span style={{ textTransform: 'capitalize' }}>{phase.replace(/_/g, ' ')}</span>
                          <span>{count}</span>
                        </div>
                        <div className="portal-mini-progress">
                          <div className="portal-mini-progress-fill" style={{ width: `${Math.max(pct, 8)}%` }} />
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <p style={{ color: '#909399', fontSize: 13 }}>No pipeline data yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default function RoleDashboard() {
  const { user, role, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [projectKind, setProjectKind] = useState('deals')
  const [dashSummary, setDashSummary] = useState(null)
  const [pipelineStats, setPipelineStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [notificationsLoading, setNotificationsLoading] = useState(true)

  const isPortal = PORTAL_ROLES.includes(role) && !isAdmin
  const isAdvisorPortal = ['Startup', 'ServiceProvider'].includes(role) && !isAdmin

  const loadNotifications = async () => {
    setNotificationsLoading(true)
    try {
      const res = await axios.get('/api/notifications?limit=8')
      setNotifications(res.data || [])
    } catch {
      setNotifications([])
    } finally {
      setNotificationsLoading(false)
    }
  }

  const handleMarkAllNotifications = async () => {
    try {
      await axios.post('/api/notifications/mark-all-read')
      setNotifications([])
      window.dispatchEvent(new Event('rs-chat-unread-changed'))
      window.dispatchEvent(new Event('rs-digest-read-changed'))
      window.dispatchEvent(new Event('rs-notifications-changed'))
    } catch (err) {
      console.error('Failed to mark all notifications read', err)
    }
  }

  const handleOpenNotification = async (item) => {
    try {
      await axios.post(`/api/notifications/${encodeURIComponent(item.id)}/read`)
      setNotifications((prev) => prev.filter((n) => n.id !== item.id))
      window.dispatchEvent(new Event('rs-chat-unread-changed'))
      window.dispatchEvent(new Event('rs-digest-read-changed'))
      window.dispatchEvent(new Event('rs-notifications-changed'))
    } catch (err) {
      console.error('Failed to mark notification read', err)
    }
    if (item.actionPath) navigate(item.actionPath)
  }

  const displayStats = (dashSummary?.stats || []).map((s) => {
    const meta = STAT_META[s.key] || { icon: 'chart', color: 'blue' }
    return { ...meta, label: s.label, value: s.value, change: s.change, key: s.key }
  })
  const recentMessages = dashSummary?.activity?.messages || []
  const upcomingMeetings = dashSummary?.activity?.meetings || []
  const displayQuickActions = (dashSummary?.quickActions || []).map((a) => ({
    label: a.label,
    path: a.path,
    icon: getCmsIcon(a.iconKey),
  }))

  const headers = PROJECT_HEADERS[role] || PROJECT_HEADERS.GP

  useEffect(() => {
    if (isAdmin) {
      navigate('/admin', { replace: true })
      return
    }
    fetchDashboard()
    if (isPortal) loadNotifications()
  }, [isAdmin, navigate, isPortal])

  const fetchDashboard = async () => {
    setLoading(true)
    try {
      const [projectsRes, summaryRes, pipeRes] = await Promise.all([
        axios.get('/api/dashboard/projects'),
        axios.get('/api/dashboard/summary'),
        role === 'GP' || role === 'LP'
          ? axios.get('/api/deals/pipeline-stats').catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ])
      setProjects(projectsRes.data.items || [])
      setProjectKind(projectsRes.data.kind || 'deals')
      setDashSummary(summaryRes.data)
      setPipelineStats(pipeRes.data)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Failed to fetch dashboard', err)
    } finally {
      setLoading(false)
    }
  }

  if (isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500 text-sm">
        Redirecting to admin…
      </div>
    )
  }

  if (isPortal && (role === 'GP' || role === 'LP')) {
    return (
      <PageShell>
        <GpLpDashboard
          user={user}
          role={role}
          loading={loading}
          displayStats={displayStats}
          projects={projects}
          recentMessages={recentMessages}
          upcomingMeetings={upcomingMeetings}
          pipelineStats={pipelineStats}
          navigate={navigate}
          notifications={notifications}
          notificationsLoading={notificationsLoading}
          onMarkAllNotifications={handleMarkAllNotifications}
          onOpenNotification={handleOpenNotification}
        />
      </PageShell>
    )
  }

  if (isAdvisorPortal) {
    return (
      <PageShell>
        <AdvisorDashboard
          user={user}
          role={role}
          loading={loading}
          displayStats={displayStats}
          projects={projects}
          recentMessages={recentMessages}
          upcomingMeetings={upcomingMeetings}
          navigate={navigate}
          notifications={notifications}
          notificationsLoading={notificationsLoading}
          onMarkAllNotifications={handleMarkAllNotifications}
          onOpenNotification={handleOpenNotification}
        />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            Welcome, {user?.system_name || user?.real_name}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {role} Dashboard
            {lastUpdated ? ` · Updated ${lastUpdated.toLocaleTimeString()}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!user?.is_paid && (
            <button
              type="button"
              onClick={() => navigate('/payment')}
              className="px-4 py-2 bg-gold-500 text-white rounded-lg text-sm font-semibold hover:bg-gold-600 transition-colors"
            >
              Upgrade to Premium
            </button>
          )}
          <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-bold">
            {user?.real_name?.charAt(0) || 'U'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-5 mb-8">
        {loading && <p className="col-span-4 text-sm text-gray-400 text-center py-6">Loading…</p>}
        {!loading && displayStats.map((stat, idx) => (
          <div key={idx} className="card p-5">
            <div className="flex justify-between items-start mb-3">
              <span className="text-xs font-medium text-gray-500">{stat.label}</span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600`}>
                <RsIcon name={stat.icon} size="md" />
              </div>
            </div>
            <p className="text-2xl font-extrabold text-navy">{stat.value}</p>
            <p className="text-xs font-semibold mt-1 text-gray-500">{stat.change}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        {displayQuickActions.map((action, idx) => {
          const Icon = action.icon
          return (
            <button
              key={idx}
              type="button"
              onClick={() => navigate(action.path)}
              className="card p-4 flex items-center gap-3 hover:shadow-md transition-all text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary-500" />
              </div>
              <span className="text-sm font-semibold text-navy">{action.label}</span>
            </button>
          )
        })}
      </div>

      <div className="card p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-navy">{headers.title}</h3>
          <button type="button" onClick={() => navigate(projectKind === 'assets' ? '/assets' : '/deals')} className="text-sm text-primary-500">
            View All
          </button>
        </div>
        {projects.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No active projects</p>
        ) : (
          <ul className="space-y-2">
            {projects.map((item) => (
              <li key={item.id}>
                <button type="button" className="w-full text-left p-3 rounded-lg hover:bg-gray-50" onClick={() => navigate(item.path)}>
                  <span className="font-semibold text-sm">{item.title}</span>
                  <span className="text-xs text-gray-500 ml-2">{item.progress || 0}%</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  )
}
